import { flexRender, Row } from '@tanstack/react-table';
import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { Spacer } from 'src/common';
import { MOTION_TRANSITION } from 'src/config';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';

const Tr = styled.tr<{ $itemHeight: number; $cursorPointer: boolean; $disabled?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  border-top: 1px solid ${({ theme }) => theme.colors.stroke};
  height: ${({ $itemHeight }) => $itemHeight}px !important;
  cursor: ${({ $cursorPointer }) => ($cursorPointer ? 'pointer' : 'default')};
  ${({ $disabled }) =>
    $disabled &&
    css`
      div,
      span,
      p,
      a {
        color: gray !important;
      }
    `};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px !important;
    height: ${({ $itemHeight }) => $itemHeight * 0.8}px !important;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: ${({ $itemHeight }) => $itemHeight * 0.6}px !important;
    font-size: 11px !important;
  }
`;

interface TdContentProps {
  $size?: number;
  $maxSize?: number;
  $minSize?: number;
}

const TdContent = styled.div<TdContentProps>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExpandTr = styled.tr`
  ::-webkit-scrollbar {
    width: 0px !important;
  }

  .ext-content {
    margin: 0 20px;
    overflow-y: hidden;
    display: grid;
    max-width: 100%;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .ext-content {
      margin: 0 10px;
    }
  }
`;

interface GTRowProps<T> {
  row: Row<T>;
  content?: React.ReactNode;
  itemHeight: number;
  disabled?: boolean;
  tableId?: string;
  hideExpandContentWhenClosed?: boolean;
}

export const GTRow = <T,>({
  row,
  content,
  itemHeight,
  disabled,
  tableId,
  hideExpandContentWhenClosed,
}: GTRowProps<T>) => {
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  return (
    <Fragment key={row.id}>
      <Tr
        $itemHeight={itemHeight}
        data-cy={`gtrow-${tableId}`}
        data-expanded={isExpanded}
        onClick={() => {
          if (canExpand) {
            row.toggleExpanded();
            mpEvent(MPEvents.TableRowExpand, { value: !isExpanded ? 'open' : 'close' });
          }
        }}
        $cursorPointer={canExpand}
        $disabled={disabled}
      >
        <td className="gt-left-spacer" />
        {row.getVisibleCells().map((cell) => {
          const { size, maxSize, minSize } = cell.column.columnDef;
          return (
            <td
              key={cell.id}
              style={{
                maxWidth: maxSize,
              }}
            >
              <TdContent $size={size} $maxSize={maxSize} $minSize={minSize} data-cy="gt-row-content">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TdContent>
            </td>
          );
        })}
      </Tr>
      {content && (
        <ExpandTr>
          <td colSpan={row.getVisibleCells().length + 1}>
            <motion.div
              className="ext-content"
              initial={{ height: 0, marginTop: 0, marginBottom: 0 }}
              animate={{
                height: isExpanded ? 'unset' : 0,
                marginTop: isExpanded ? 20 : 0,
                marginBottom: isExpanded ? 20 : 0,
              }}
              transition={MOTION_TRANSITION}
            >
              <Spacer $px={5} />
              <AnimatePresence>
                {(hideExpandContentWhenClosed ? isExpanded : true) && (
                  <motion.div initial={false} exit={{ opacity: 0 }} transition={{ delay: 0.3, duration: 0 }}>
                    {content}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </td>
        </ExpandTr>
      )}
    </Fragment>
  );
};
